import React from "react";
import { Check, Minus } from "lucide-react";

/**
 * ComparisonTable — "Workforce Members vs Generic AI Chat Windows & Copilots"
 * Sprint Ib · B3 polish:
 *  - violet-tinted column separator (not solid)
 *  - subtle violet-glow row hover on both cells
 *  - right column visually dominant (violet accent border-left + violet check icons)
 *  - left column muted (neutral dash icons)
 */
const ROWS = [
    {
        generic: { key: "REACTIVE",             desc: "Waits for your prompt to do anything." },
        worker:  { key: "PROACTIVE",            desc: "Acts on its own, follows up, reports back." },
    },
    {
        generic: { key: "CHAT WINDOW",          desc: "Lives inside a single interface." },
        worker:  { key: "EVERYWHERE",           desc: "Email, phone, Slack, Teams — meets people where they are." },
    },
    {
        generic: { key: "SPARSE MEMORY",        desc: "Forgets context between sessions." },
        worker:  { key: "FULL BUSINESS CONTEXT", desc: "Knows your clients, history, and goals — always." },
    },
    {
        generic: { key: "PERSONAL ASSISTANT",   desc: "Helps you do your job." },
        worker:  { key: "TEAM MATE",            desc: "Does the job — autonomously." },
    },
    {
        generic: { key: "YOU DEFINE EVERYTHING", desc: "Skills, workflow, steps — you build it." },
        worker:  { key: "SELF-DIRECTED",         desc: "Set a goal. It figures out the how." },
    },
];

export default function ComparisonTable() {
    return (
        <section
            className="max-w-6xl mx-auto px-6 py-24"
            data-testid="comparison-section"
            style={{ background: "transparent" }}
        >
            <div className="text-center mb-10">
                <div
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em]"
                    style={{ color: "var(--atlas-primary-hover)" }}
                >
                    <span className="atlas-section-dot" /> Workforce Members: the smarter choice
                </div>
                <h2
                    className="atlas-text-gradient mt-4 text-4xl sm:text-5xl font-bold tracking-tight"
                    data-testid="comparison-h2"
                >
                    Not another prompt window. A digital workforce.
                </h2>
                <p
                    className="mt-3 text-[15px] max-w-xl mx-auto"
                    style={{ color: "var(--atlas-text-secondary)" }}
                >
                    Generic AI chat windows and copilots sit inside a single UI. Workforce Members show up wherever work happens.
                </p>
            </div>

            <div
                className="atlas-card rounded-2xl overflow-hidden"
                data-testid="comparison-table-frame"
                style={{ padding: 0 }}
            >
                {/* Column headers */}
                <div
                    className="grid grid-cols-2"
                    style={{
                        borderBottom: "1px solid rgba(139,92,246,0.28)",
                        background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
                    }}
                >
                    <div
                        className="px-6 py-4 text-center"
                        style={{ borderRight: "1px solid rgba(139,92,246,0.28)" }}
                    >
                        <div
                            className="text-[11px] font-semibold uppercase tracking-[0.14em]"
                            style={{ color: "var(--atlas-text-muted)" }}
                        >
                            Generic AI Chat Windows &amp; Copilots
                        </div>
                    </div>
                    <div
                        className="px-6 py-4 text-center relative"
                        style={{
                            background: "linear-gradient(180deg, rgba(139,92,246,0.14), rgba(139,92,246,0.06))",
                            boxShadow: "inset 3px 0 0 var(--atlas-primary)",
                        }}
                    >
                        <div
                            className="text-[11px] font-semibold uppercase tracking-[0.14em] inline-flex items-center gap-1.5"
                            style={{ color: "var(--atlas-primary-hover)" }}
                        >
                            <span
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ background: "var(--atlas-primary)", boxShadow: "0 0 8px var(--atlas-primary-glow)" }}
                            />
                            Workforce Members
                        </div>
                    </div>
                </div>

                {/* Rows */}
                <div data-testid="comparison-rows">
                    {ROWS.map((row, i) => (
                        <div
                            key={i}
                            className="comparison-row grid grid-cols-2 group"
                            data-testid={`comparison-row-${i}`}
                            style={{
                                borderBottom: i === ROWS.length - 1 ? "none" : "1px solid rgba(139,92,246,0.14)",
                            }}
                        >
                            {/* LEFT — muted */}
                            <div
                                className="px-6 py-5 group-hover:bg-white/[0.02] transition-colors"
                                style={{ borderRight: "1px solid rgba(139,92,246,0.28)" }}
                            >
                                <div className="flex items-start gap-2.5">
                                    <span
                                        className="mt-0.5 w-4 h-4 rounded-full grid place-items-center flex-shrink-0"
                                        style={{
                                            background: "rgba(148,163,184,0.12)",
                                            border: "1px solid rgba(148,163,184,0.32)",
                                        }}
                                    >
                                        <Minus className="w-2.5 h-2.5" strokeWidth={2.5} style={{ color: "var(--atlas-text-muted)" }} />
                                    </span>
                                    <div className="min-w-0">
                                        <div
                                            className="text-[10.5px] font-semibold uppercase tracking-[0.14em]"
                                            style={{ color: "var(--atlas-text-muted)" }}
                                        >
                                            {row.generic.key}
                                        </div>
                                        <div
                                            className="mt-1 text-[13.5px] leading-snug"
                                            style={{ color: "var(--atlas-text-tertiary)" }}
                                        >
                                            {row.generic.desc}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT — dominant */}
                            <div
                                className="px-6 py-5 relative group-hover:bg-[rgba(139,92,246,0.09)] transition-colors"
                                style={{
                                    background: "rgba(139,92,246,0.05)",
                                    boxShadow: "inset 3px 0 0 rgba(139,92,246,0.55)",
                                }}
                            >
                                <div className="flex items-start gap-2.5">
                                    <span
                                        className="mt-0.5 w-4 h-4 rounded-full grid place-items-center flex-shrink-0"
                                        style={{
                                            background: "var(--atlas-primary-muted)",
                                            border: "1px solid rgba(139,92,246,0.5)",
                                            boxShadow: "0 0 6px rgba(139,92,246,0.4)",
                                        }}
                                    >
                                        <Check className="w-2.5 h-2.5" strokeWidth={3} style={{ color: "var(--atlas-primary-hover)" }} />
                                    </span>
                                    <div className="min-w-0">
                                        <div
                                            className="text-[10.5px] font-semibold uppercase tracking-[0.14em]"
                                            style={{ color: "var(--atlas-primary-hover)" }}
                                        >
                                            {row.worker.key}
                                        </div>
                                        <div
                                            className="mt-1 text-[13.5px] leading-snug"
                                            style={{ color: "var(--atlas-text-primary)" }}
                                        >
                                            {row.worker.desc}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
